import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { LucideIcon, Plus } from "lucide-react";
import { motion } from "framer-motion";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
  };
  className?: string;
  children?: ReactNode;
  variant?: "default" | "minimal" | "card";
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  children,
  variant = "default",
}: EmptyStateProps) {
  const ActionIcon = action?.icon || Plus;

  if (variant === "minimal") {
    return (
      <div className={cn("text-center py-8 px-4", className)}>
        <Icon className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">{title}</p>
        {action && (
          <Button
            onClick={action.onClick}
            variant="link"
            size="sm"
            className="mt-2"
          >
            {action.label}
          </Button>
        )}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "flex flex-col items-center justify-center py-12 px-4 text-center",
        variant === "card" && "bg-muted/30 rounded-xl border border-dashed border-border/50",
        className
      )}
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="relative"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 mb-4 shadow-sm">
          <Icon className="h-7 w-7 text-primary-text/70" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-muted rounded-full border-2 border-background" />
      </motion.div>
      <h3 className="text-lg font-semibold mb-1.5 text-balance">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-xs mb-5 text-balance leading-relaxed">
        {description}
      </p>
      {action && (
        <Button
          onClick={action.onClick}
          size="sm"
          className="gap-2 shadow-sm"
        >
          <ActionIcon className="h-4 w-4" />
          {action.label}
        </Button>
      )}
      {children}
    </motion.div>
  );
}
